import { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import { categories, articles } from '@/mocks/articles';

export default function Category() {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialTab = searchParams.get('tab') || 'all';
  const [activeTab, setActiveTab] = useState(initialTab);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [activeTab]);

  const handleTabChange = (slug: string) => {
    setActiveTab(slug);
    setSearchParams(slug === 'all' ? {} : { tab: slug });
  };

  const filteredArticles =
    activeTab === 'all'
      ? articles
      : articles.filter((a) => a.category === activeTab);

  const activeCategory = categories.find((c) => c.slug === activeTab);

  const categorySchema = [
    {
      '@context': 'https://sayaworks.github.io',
      '@type': 'CollectionPage',
      name: activeTab === 'all' ? '佐山爱全部内容分类' : `佐山爱${activeCategory?.name || ''}分类`,
      description: activeCategory?.description || '浏览佐山爱相关内容分类',
      url: `https://sayaworks.github.io/category${activeTab !== 'all' ? `?tab=${activeTab}` : ''}`,
    },
  ];

  return (
    <>
      <head>
        <title>{activeTab === 'all' ? '佐山爱作品分类 — 浏览全部内容' : `佐山爱${activeCategory?.name || ''} — ${activeCategory?.description || ''}`} | 佐山爱官方资料站</title>
        <meta name="description" content={activeCategory?.description || '浏览佐山爱全部作品与资讯分类'} />
        <meta name="keywords" content={`佐山爱,${activeCategory?.name || '作品分类'},佐山爱资料,佐山爱内容`} />
        <link rel="canonical" href={`https://sayaworks.github.io/category${activeTab !== 'all' ? `?tab=${activeTab}` : ''}`} />
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(categorySchema) }}
        />
      </head>
      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="pt-24 md:pt-28">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <Breadcrumb
              items={[
                { label: '首页', path: '/' },
                { label: '分类浏览' },
              ]}
            />

            {/* Header */}
            <div className="mb-8">
              <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">
                内容分类
              </h1>
              <p className="text-sm text-foreground-500 mt-2">
                按分类浏览，找到你感兴趣的佐山爱相关内容
              </p>
            </div>

            {/* Tab Switcher */}
            <div className="flex flex-wrap gap-2 mb-10">
              {categories.map((cat) => (
                <button
                  key={cat.id}
                  onClick={() => handleTabChange(cat.slug)}
                  className={`px-4 py-2 rounded-full text-sm font-body transition-all duration-200 whitespace-nowrap cursor-pointer ${
                    activeTab === cat.slug
                      ? 'bg-primary-500 text-background-50'
                      : 'bg-background-100 text-foreground-600 hover:bg-background-200/70'
                  }`}
                >
                  <i className={`${cat.icon} mr-1.5 text-xs`}></i>
                  {cat.name}
                  <span className={`ml-1.5 text-xs ${activeTab === cat.slug ? 'text-background-50/70' : 'text-foreground-400'}`}>
                    {cat.count}
                  </span>
                </button>
              ))}
            </div>

            {/* Articles Grid */}
            {filteredArticles.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6 mb-16">
                {filteredArticles.map((article) => (
                  <Link
                    key={article.id}
                    to={`/article/${article.id}`}
                    className="group bg-background-50 border border-background-200/70 rounded-xl overflow-hidden hover:border-primary-300 transition-all duration-200 cursor-pointer"
                  >
                    <div className="h-44 overflow-hidden">
                      <img
                        src={article.cardImage}
                        alt={article.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <div className="p-5">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="inline-block px-2 py-0.5 text-xs font-medium rounded-full bg-secondary-100 text-secondary-700 whitespace-nowrap">
                          {article.categoryName}
                        </span>
                      </div>
                      <h3 className="font-heading text-sm font-semibold text-foreground-950 group-hover:text-primary-600 transition-colors duration-150 line-clamp-2 mb-2">
                        {article.title}
                      </h3>
                      <p className="text-xs text-foreground-500 leading-relaxed line-clamp-2 mb-3">
                        {article.summary}
                      </p>
                      <div className="flex items-center gap-3 text-xs text-foreground-400">
                        <span>{article.date}</span>
                        <span>{article.readTime} 分钟阅读</span>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            ) : (
              <div className="text-center py-20 mb-16">
                <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100">
                  <i className="ri-inbox-line text-2xl text-foreground-300"></i>
                </div>
                <p className="text-foreground-500">该分类暂无内容</p>
              </div>
            )}
          </div>
        </main>

        <Footer />
      </div>
    </>
  );
}